import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TaskManagementPanel = ({ notification, onUpdateTask, onClose }) => {
  const [expandedTask, setExpandedTask] = useState(null);

  const getTaskStatusColor = (status) => {
    const colors = {
      'pending': 'bg-warning/10 text-warning border-warning/20',
      'in_progress': 'bg-primary/10 text-primary border-primary/20',
      'completed': 'bg-success/10 text-success border-success/20',
      'overdue': 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const getTaskStatusLabel = (status) => {
    const labels = {
      'pending': 'قيد الانتظار',
      'in_progress': 'قيد التنفيذ',
      'completed': 'مكتمل',
      'overdue': 'متأخر'
    };
    return labels?.[status] || status;
  };

  const handleTaskAction = (taskId, newStatus) => {
    onUpdateTask(notification?.id, taskId, newStatus);
  };

  if (!notification?.tasks || notification?.tasks?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 text-center">
        <Icon name="CheckCircle" size={40} className="mx-auto mb-3 text-muted-foreground opacity-50" />
        <p className="text-sm text-muted-foreground">لا توجد مهام مرتبطة بهذا التنبيه</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-base font-heading font-semibold text-foreground flex items-center gap-2">
          <Icon name="ListTodo" size={18} className="text-primary" />
          <span>المهام المطلوبة (<span className="font-data" dir="ltr">{notification?.tasks?.length}</span>)</span>
        </h4>
      </div>
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {notification?.tasks?.map((task, index) => (
            <motion.div
              key={task?.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ delay: index * 0.05 }}
              className="bg-muted/30 rounded-lg border border-border overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <h5 className="text-sm font-semibold text-foreground mb-1">
                      {task?.title}
                    </h5>
                    <p className="text-xs text-muted-foreground">
                      {task?.description}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-md text-xs font-medium border whitespace-nowrap ${getTaskStatusColor(task?.status)}`}>
                    {getTaskStatusLabel(task?.status)}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                  <div className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} className="text-primary" />
                    <span className="font-data" dir="ltr">الموعد: {task?.dueDate}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} className="text-warning" />
                    <span>الأولوية: {task?.priority === 'high' ? 'عالية' : task?.priority === 'medium' ? 'متوسطة' : 'منخفضة'}</span>
                  </div>
                </div>

                {/* Task Actions */}
                <div className="flex items-center gap-2">
                  {task?.status === 'pending' && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Play"
                      onClick={() => handleTaskAction(task?.id, 'in_progress')}
                    >
                      بدء المهمة
                    </Button>
                  )}
                  {task?.status === 'in_progress' && (
                    <Button
                      variant="success"
                      size="sm"
                      iconName="CheckCircle"
                      onClick={() => handleTaskAction(task?.id, 'completed')}
                    >
                      إكمال المهمة
                    </Button>
                  )}
                  {task?.status === 'completed' && (
                    <div className="flex items-center gap-2 text-success text-sm">
                      <Icon name="CheckCircle2" size={16} />
                      <span className="font-medium">تم الإكمال</span>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName={expandedTask === task?.id ? 'ChevronUp' : 'ChevronDown'}
                    onClick={() => setExpandedTask(expandedTask === task?.id ? null : task?.id)}
                  >
                    {expandedTask === task?.id ? 'إخفاء' : 'تفاصيل'}
                  </Button>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {expandedTask === task?.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-3 pt-3 border-t border-border"
                    >
                      <div className="space-y-2 text-xs">
                        <div className="flex items-start gap-2">
                          <Icon name="Info" size={14} className="text-primary mt-0.5" />
                          <div>
                            <p className="font-medium text-foreground mb-1">معلومات إضافية:</p>
                            <p className="text-muted-foreground">
                              هذه المهمة مرتبطة بالعميل {notification?.clientName} والعقار {notification?.propertyTitle}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      {/* Task Summary */}
      <div className="bg-primary/5 rounded-lg border border-primary/20 p-2">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[10px] text-muted-foreground mb-0.5">قيد الانتظار</p>
            <p className="text-base font-bold text-warning font-data" dir="ltr">
              {notification?.tasks?.filter(t => t?.status === 'pending')?.length}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground mb-0.5">قيد التنفيذ</p>
            <p className="text-base font-bold text-primary font-data" dir="ltr">
              {notification?.tasks?.filter(t => t?.status === 'in_progress')?.length}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground mb-0.5">مكتمل</p>
            <p className="text-base font-bold text-success font-data" dir="ltr">
              {notification?.tasks?.filter(t => t?.status === 'completed')?.length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskManagementPanel;
